import * as React from 'react'

import { cn } from './cn'

export interface SnackbarProps {
  open?: boolean
  onClose?: (event?: unknown, reason?: 'timeout' | 'clickaway') => void
  message?: React.ReactNode
  action?: React.ReactNode
  autoHideDuration?: number | null
  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
  className?: string
  children?: React.ReactElement
}

/**
 * Fixed-position toast matching the old MUI Snackbar call sites.
 * When `children` is given (e.g. an Alert), it replaces the default surface.
 */
const Snackbar = ({
  open,
  onClose,
  message,
  action,
  autoHideDuration = null,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  className,
  children,
}: SnackbarProps) => {
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose

  React.useEffect(() => {
    if (!open || autoHideDuration == null) return undefined
    const timer = setTimeout(
      () => onCloseRef.current?.(undefined, 'timeout'),
      autoHideDuration,
    )
    return () => clearTimeout(timer)
  }, [open, autoHideDuration])

  if (!open) return null

  return (
    <div
      className={cn(
        'fixed z-[1400] flex animate-ps-slide-up',
        anchorOrigin.vertical === 'bottom' ? 'bottom-6' : 'top-6',
        anchorOrigin.horizontal === 'left' && 'left-6',
        anchorOrigin.horizontal === 'right' && 'right-6',
        anchorOrigin.horizontal === 'center' && 'left-1/2 -translate-x-1/2',
        className,
      )}
    >
      {children ?? (
        <div
          role="alert"
          className="flex min-w-[288px] max-w-[calc(100vw-48px)] items-center gap-2 rounded-xl border border-white/10 bg-[#211826]/95 px-4 py-2.5 text-sm font-medium text-white shadow-menu backdrop-blur-md dark:border-divider dark:bg-paper/95 dark:text-text-primary"
        >
          <span className="grow py-1">{message}</span>
          {action}
        </div>
      )}
    </div>
  )
}

export default Snackbar
